import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { hasAboutContent } from "@alpha/shared";
import { useAuth } from "../lib/auth";
import { useHasAnyTournamentRole, useIsAdmin } from "../lib/me";
import { useBranding } from "../lib/siteSettings";
import { Button, ButtonLink, DiamondMark, cx } from "../components/ui";
import { loginPath } from "../lib/nextParam";

/**
 * The nav is the app's most visible statement of who the caller is, so it shows
 * each visitor only what they are in: everyone browses, accounts get a
 * dashboard, and whoever the console lets in gets the console. That last one is
 * two questions, not one tier — the global allowlist, or a role granted on a
 * single tournament — which is why `hasConsole` arrives already combined. The
 * Admin link stays hidden while the session query is in flight rather than
 * appearing and vanishing.
 */
function useNavLinks(signedIn: boolean, hasConsole: boolean, hasAbout: boolean) {
  return useMemo(() => {
    const links = [
      { to: "/", label: "Home", end: true },
      { to: "/tournaments", label: "Tournaments", end: false },
      // Ungated, unlike About below: this page's copy is in its component, so
      // the link can never outlive its content.
      { to: "/host", label: "Host an event", end: false },
    ];
    // With the public pair, ahead of the account tiers — and only once an admin
    // has written something, so the link never leads to an empty page.
    if (hasAbout) links.push({ to: "/about", label: "About", end: false });
    if (signedIn) links.push({ to: "/me", label: "Dashboard", end: false });
    if (hasConsole) links.push({ to: "/admin", label: "Admin", end: false });
    return links;
  }, [signedIn, hasConsole, hasAbout]);
}

export function Layout() {
  const { idToken, email, ready, signOut } = useAuth();
  const branding = useBranding();
  const showAbout = hasAboutContent(branding.about);
  // The same question AdminLayout's gate asks (spec §3, §6): the allowlist, or
  // a role granted on one tournament. A nav that offered the console to the
  // allowlist alone would leave a tournament admin — who the gate lets
  // straight through — typing /admin by hand to reach their own event.
  //
  // Both hooks are called before either answer is used, NOT combined inline
  // with `||`: each one subscribes to the session query, so short-circuiting
  // the second would render fewer hooks on the render where the first turns
  // true — which for an admin is the render the session resolves on, and React
  // throws rather than tolerating it. AdminLayout asks the pair the same way.
  const isAdmin = useIsAdmin();
  const hasTournamentRole = useHasAnyTournamentRole();
  const hasConsole = isAdmin || hasTournamentRole;
  const navLinks = useNavLinks(!!idToken, hasConsole, showAbout);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    document.title = branding.orgName;
  }, [branding.orgName]);

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 text-slate-800">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-navy-950 text-white supports-[backdrop-filter]:bg-navy-950/90 supports-[backdrop-filter]:backdrop-blur">
        <div className="mx-auto flex h-14 max-w-screen-2xl items-center justify-between gap-3 px-4 sm:h-16 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="group flex items-center gap-2.5 font-display text-base font-bold tracking-tight"
          >
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt=""
                className="h-7 w-7 rounded object-contain transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105"
              />
            ) : (
              <DiamondMark className="h-7 w-7 transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105" />
            )}
            <span>{branding.orgName}</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-6 md:flex">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  cx(
                    "relative py-1 text-sm font-medium transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-brand-400 after:transition-transform after:duration-200",
                    isActive
                      ? "text-white after:scale-x-100"
                      : "text-slate-300 hover:text-white hover:after:scale-x-100",
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            {ready &&
              (idToken ? (
                <>
                  <span className="max-w-48 truncate text-xs text-slate-400">{email}</span>
                  <Button variant="onDark" size="sm" onClick={signOut}>
                    Sign out
                  </Button>
                </>
              ) : (
                <ButtonLink variant="onDark" size="sm" to={loginPath(pathname)}>
                  Sign in
                </ButtonLink>
              ))}
          </div>

          {/* Mobile hamburger (morphs into an X) */}
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-white/10 md:hidden"
          >
            <span
              className={cx(
                "absolute h-0.5 w-5 rounded-full bg-current transition-all duration-300",
                menuOpen ? "rotate-45" : "-translate-y-[5px]",
              )}
            />
            <span
              className={cx(
                "absolute h-0.5 w-5 rounded-full bg-current transition-all duration-300",
                menuOpen && "scale-x-0 opacity-0",
              )}
            />
            <span
              className={cx(
                "absolute h-0.5 w-5 rounded-full bg-current transition-all duration-300",
                menuOpen ? "-rotate-45" : "translate-y-[5px]",
              )}
            />
          </button>
        </div>

        {/* Mobile menu panel */}
        {menuOpen ? (
          <nav className="animate-slide-down border-t border-white/10 px-4 pb-4 pt-2 md:hidden">
            <div className="space-y-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    cx(
                      "block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white",
                    )
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
            {ready ? (
              <div className="mt-3 border-t border-white/10 pt-3">
                {idToken ? (
                  <div className="flex items-center justify-between gap-3 px-3">
                    <span className="min-w-0 truncate text-xs text-slate-400">{email}</span>
                    <Button variant="onDark" size="sm" onClick={signOut}>
                      Sign out
                    </Button>
                  </div>
                ) : (
                  <ButtonLink variant="onDark" size="sm" to={loginPath(pathname)} className="w-full">
                    Sign in
                  </ButtonLink>
                )}
              </div>
            ) : null}
          </nav>
        ) : null}
      </header>

      {/*
        One shell width for the header, the page, and the footer — 1536px, so
        the logo, the content, and the footer all sit on the same two edges.
        Change it in three places or not at all.

        A tournament is a grid: pools by teams by times by fields. Every screen
        here is a table folded into one column to survive a phone, so the width
        this frame hands down is only worth having where a page unfolds it into
        more columns. Pages that are reading rather than operating — forms, the
        wizard, sign-in — clamp themselves narrower on purpose.
      */}
      <main className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-screen-2xl flex-col items-center justify-between gap-2 px-4 py-6 text-xs text-slate-400 sm:flex-row sm:px-6 lg:px-8">
          <span className="flex items-center gap-1.5">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="h-4 w-4 rounded object-contain opacity-70" />
            ) : (
              <DiamondMark className="h-4 w-4 opacity-70" />
            )}
            {branding.orgName}
          </span>
          <span className="flex items-center gap-3">
            {showAbout ? (
              <Link to="/about" className="transition-colors hover:text-slate-600">
                About
              </Link>
            ) : null}
            <span>{branding.tagline}</span>
          </span>
        </div>
      </footer>
    </div>
  );
}
